import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, RotateCcw, Trash2, Building2, KeyRound } from 'lucide-react';

export default function GestoreLicenses() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [confirm, setConfirm] = useState(null); // { title, description, action, destructive }

  const load = async () => { const { data } = await adminApi.get('/admin/licenses'); setItems(data); };
  useEffect(() => { load(); }, []);

  const ask = (cfg) => setConfirm(cfg);
  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (c?.action) await c.action();
  };

  const add = async () => {
    if (!newKey.trim()) { toast.error(t('gestore.lEnterKey')); return; }
    try { await adminApi.post('/admin/licenses', { key: newKey.trim() }); setNewKey(''); load(); toast.success(t('gestore.lKeyAdded')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.lError')); }
  };

  const doReset = async (k) => {
    try { await adminApi.post(`/admin/licenses/${k}/reset`); load(); toast.success(t('gestore.lKeyReset')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.lError')); }
  };
  const doDelete = async (k) => {
    try { await adminApi.delete(`/admin/licenses/${k}`); load(); toast.success(t('gestore.lKeyDeleted')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.lError')); }
  };

  const toggle = async (l) => {
    try {
      const { data } = await adminApi.post(`/admin/licenses/${l.key}/toggle`);
      load();
      toast.success(data.enabled ? t('gestore.lLicenseEnabled') : t('gestore.lLicenseDisabled'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('gestore.lError')); }
  };

  const assignedCount = items.filter((i) => i.restaurant_id).length;

  const askReset = (l) => ask({
    title: t('gestore.lResetTitle', { key: l.key }),
    description: t('gestore.lResetDesc', { name: l.restaurant_name }),
    action: () => doReset(l.key),
  });
  const askDelete = (l) => ask({
    title: t('gestore.lDeleteTitle', { key: l.key }),
    description: l.restaurant_id ? t('gestore.lDeleteDescAssigned', { name: l.restaurant_name }) : t('gestore.lDeleteDescFree'),
    destructive: true,
    action: () => doDelete(l.key),
  });

  const StatusBadge = ({ l }) => (
    l.restaurant_id
      ? <Badge variant="default">{t('gestore.lAssigned')}</Badge>
      : <Badge variant="secondary">{t('gestore.lAvailable')}</Badge>
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
      <GestorePageHeader title={t('gestore.lTitle')} subtitle={t('gestore.lSubtitle', { total: items.length, assigned: assignedCount })} />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:max-w-md">
          <Input value={newKey} onChange={(e) => setNewKey(e.target.value.toUpperCase())} placeholder={t('gestore.lKeyPlaceholder')} className="font-mono uppercase" data-testid="license-add-input" />
          <Button onClick={add} data-testid="license-add-button" className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-1" /> {t('gestore.lAdd')}</Button>
        </div>
      </Card>

      {/* Desktop table */}
      <Card className="p-0 hidden md:block overflow-hidden">
        <Table data-testid="gestore-licenses-table">
          <TableHeader><TableRow><TableHead>{t('gestore.lColKey')}</TableHead><TableHead>{t('gestore.lColRestaurant')}</TableHead><TableHead>{t('gestore.lColStatus')}</TableHead><TableHead>{t('gestore.lColEnabled')}</TableHead><TableHead className="text-right">{t('gestore.lColActions')}</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow key={l.key} data-testid={`license-row-${l.key}`}>
                <TableCell className="font-mono text-sm">{l.key}</TableCell>
                <TableCell className="text-sm">
                  {l.restaurant_name ? (
                    <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {l.restaurant_name}</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell><StatusBadge l={l} /></TableCell>
                <TableCell>
                  <Switch checked={l.enabled !== false} onCheckedChange={() => toggle(l)} disabled={!l.restaurant_id} data-testid={`license-toggle-${l.key}`} />
                </TableCell>
                <TableCell className="text-right">
                  {l.restaurant_id && (
                    <Button variant="ghost" size="icon" title={t('gestore.lResetFree')} data-testid={`license-reset-${l.key}`} onClick={() => askReset(l)}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="text-destructive" data-testid={`license-delete-${l.key}`} onClick={() => askDelete(l)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden flex flex-col gap-3" data-testid="gestore-licenses-card-list">
        {items.map((l) => (
          <Card key={l.key} className="p-4" data-testid={`gestore-license-card-${l.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-medium flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> {l.key}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 truncate">
                  <Building2 className="h-3 w-3 shrink-0" /> {l.restaurant_name || '—'}
                </div>
              </div>
              <StatusBadge l={l} />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={l.enabled !== false} onCheckedChange={() => toggle(l)} disabled={!l.restaurant_id} data-testid={`license-toggle-mobile-${l.key}`} />
                <span className="text-muted-foreground">{l.enabled !== false ? t('gestore.rEnabled') : t('gestore.rDisabled')}</span>
              </label>
              <div className="flex items-center gap-1">
                {l.restaurant_id && (
                  <Button variant="ghost" size="icon" title={t('gestore.lResetFree')} data-testid={`license-reset-mobile-${l.key}`} onClick={() => askReset(l)}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="text-destructive" data-testid={`license-delete-mobile-${l.key}`} onClick={() => askDelete(l)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">{t('gestore.lCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="confirm-ok"
              className={confirm?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}>
              {t('gestore.lConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
